import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Logo from '../components/styleguide/Logo';
import FormInput from '../components/common/FormInput';
import PasswordInput from '../components/common/PasswordInput';
import ErrorAlert from '../components/common/ErrorAlert';
import { useAuthForm } from '../hooks/useAuthForm';
import apiService from '../services/apiService';
import ROUTES from '../constants/routes';

/**
 * Página de registro de nuevos usuarios
 *
 * Crea cuenta y redirige a verificación de email.
 * Valida email, contraseña y confirmación de contraseña.
 *
 * @returns {JSX.Element}
 */
function Register() {
  const navigate = useNavigate();

  const {
    formData,
    errors,
    setErrors,
    isLoading,
    setIsLoading,
    showPassword,
    togglePassword,
    showConfirmPassword,
    toggleConfirmPassword,
    handleChange,
    validateEmail,
    validatePassword,
    validateConfirmPassword
  } = useAuthForm({
    email: '',
    password: '',
    confirmPassword: '',
    nombre: ''
  });

  /**
   * Valida campos del formulario de registro
   * @returns {Object} Objeto con errores por campo
   */
  const validateForm = () => {
    const newErrors = {};

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    const confirmError = validateConfirmPassword(formData.password, formData.confirmPassword);
    if (confirmError) newErrors.confirmPassword = confirmError;

    return newErrors;
  };

  /**
   * Maneja envío del formulario de registro
   * @param {Event} e - Evento del formulario
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      const data = await apiService.register({
        email: formData.email,
        password: formData.password,
        nombre: formData.nombre || undefined
      });

      if (data.success) {
        navigate(ROUTES.VERIFY_EMAIL, {
          state: { email: formData.email, mensaje: data.message }
        });
      } else {
        setErrors({ general: data.message || 'Error al registrar' });
      }
    } catch (error) {
      setErrors({ general: error.message || 'Error de conexion' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <Logo size="small" />
                    <p className="login-subtitle">Registrate para acceder al sistema</p>
        </div>

        <ErrorAlert message={errors.general} />

        <form onSubmit={handleSubmit} className="login-form">
          <FormInput
            label="Nombre (opcional)"
            icon="user"
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            placeholder="Tu nombre"
            disabled={isLoading}
          />

          <FormInput
            label="Email corporativo"
            icon="envelope"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            placeholder="correo@empresa.cl"
            disabled={isLoading}
          />

          <PasswordInput
            label="Contrasena"
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            placeholder="Minimo 6 caracteres"
            disabled={isLoading}
            showPassword={showPassword}
            onToggle={togglePassword}
          />

          <PasswordInput
            label="Confirmar contrasena"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={errors.confirmPassword}
            placeholder="Repite tu contrasena"
            disabled={isLoading}
            showPassword={showConfirmPassword}
            onToggle={toggleConfirmPassword}
          />

          <button
            type="submit"
            className="btn btn-primary btn-block btn-lg"
            disabled={isLoading}
          >
            {isLoading ? (
              <><i className="fas fa-spinner fa-spin"></i> Registrando...</>
            ) : (
              <><i className="fas fa-user-plus"></i> Crear cuenta</>
            )}
          </button>
        </form>

        <div className="login-footer">
          <p className="text-muted margin-bottom-sm">
            Ya tienes cuenta?
          </p>
          <Link to={ROUTES.LOGIN} className="login-link">
            <i className="fas fa-sign-in-alt"></i> Iniciar sesion
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Register;
